import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Center,
  Skeleton,
  Alert,
  Pagination,
  Anchor,
  SegmentedControl,
  Card,
  Image,
  Badge,
  Box,
  Rating,
  SimpleGrid,
} from '@mantine/core';
import { IconAlertCircle, IconChevronRight, IconMoodEmpty, IconPackage } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { productsService } from '../../services/products';
import { CATEGORY_EMOJI } from '../../utils/categories';

const SORT_OPTIONS = [
  { label: 'Top Rated', value: 'top_rated' },
  { label: 'Most Reviewed', value: 'most_reviewed' },
  { label: 'Newest', value: 'newest' },
];

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('top_rated');
  const pageSize = 20;

  const decodedCategory = category ? decodeURIComponent(category) : '';
  const emoji = CATEGORY_EMOJI[decodedCategory] ?? '📦';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['products-by-category', decodedCategory, page, sort],
    queryFn: () => productsService.getByCategory(decodedCategory, page, pageSize, sort),
    enabled: !!decodedCategory,
    staleTime: 30_000,
  });

  const handleSortChange = (value: string) => {
    setSort(value);
    setPage(1);
  };

  return (
    <Container size="md" pt="md" pb="xl">
      <Stack gap="lg">

        {/* Breadcrumb */}
        <Group gap="xs">
          <Anchor component={Link} to="/categories" size="sm" c="dimmed">Categories</Anchor>
          <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />
          <Text size="sm" c="dimmed">{decodedCategory}</Text>
        </Group>

        {/* Header */}
        <Box
          p="lg"
          style={{
            borderRadius: 'var(--mantine-radius-md)',
            background: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))',
            border: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Group gap="md" align="center">
            <span style={{ fontSize: 52, lineHeight: 1 }}>{emoji}</span>
            <div>
              <Title order={2} lh={1.2}>{decodedCategory}</Title>
              <Text size="sm" c="dimmed" mt={4}>
                {isLoading ? (
                  <Skeleton w={80} h={16} radius="sm" display="inline-block" />
                ) : data ? (
                  <><strong>{data.totalCount.toLocaleString()}</strong> {data.totalCount === 1 ? 'product' : 'products'}</>
                ) : null}
              </Text>
            </div>
          </Group>
        </Box>

        {/* Sort bar */}
        <Group justify="flex-end">
          <SegmentedControl
            size="sm"
            value={sort}
            onChange={handleSortChange}
            data={SORT_OPTIONS}
          />
        </Group>

        {/* Content */}
        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} height={110} radius="md" />
            ))}
          </SimpleGrid>
        ) : isError ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load products for this category.
          </Alert>
        ) : !data || data.products.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconMoodEmpty size={40} color="var(--mantine-color-dimmed)" />
              <Text c="dimmed" fw={500}>No products in this category yet.</Text>
              <Anchor component={Link} to="/reviews/create" size="sm">
                Be the first to review something here
              </Anchor>
            </Stack>
          </Center>
        ) : (
          <Stack gap="sm">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {data.products.map((product) => (
                <Card
                  key={product.id}
                  component={Link}
                  to={`/products/${product.id}`}
                  withBorder
                  radius="md"
                  p="md"
                  style={{ textDecoration: 'none' }}
                >
                  <Group gap="md" wrap="nowrap">
                    {product.imageUrl ? (
                      <Image
                        src={product.imageUrl}
                        w={64}
                        h={64}
                        fit="contain"
                        radius="sm"
                        style={{ flexShrink: 0 }}
                      />
                    ) : (
                      <Center
                        w={64}
                        h={64}
                        style={{
                          flexShrink: 0,
                          borderRadius: 'var(--mantine-radius-sm)',
                          background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
                        }}
                      >
                        <IconPackage size={28} color="var(--mantine-color-dimmed)" />
                      </Center>
                    )}
                    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} lineClamp={2} size="sm">{product.name}</Text>
                      {product.brand && (
                        <Text size="xs" c="dimmed">{product.brand}</Text>
                      )}
                      <Group gap="xs" mt={2}>
                        {product.averageRating != null ? (
                          <>
                            <Rating value={product.averageRating} fractions={2} readOnly size="xs" />
                            <Text size="xs" c="dimmed">
                              {product.averageRating.toFixed(1)} · {product.reviewCount} {product.reviewCount === 1 ? 'review' : 'reviews'}
                            </Text>
                          </>
                        ) : (
                          <Badge size="xs" variant="light" color="gray">No reviews yet</Badge>
                        )}
                      </Group>
                    </Stack>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
            {data.totalPages > 1 && (
              <Center mt="md">
                <Pagination
                  value={page}
                  onChange={(p) => { setPage(p); window.scrollTo(0, 0); }}
                  total={data.totalPages}
                />
              </Center>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
